'use client'

import { useEffect, useState, type ReactNode } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import { Loader2 } from 'lucide-react'
import {
  AUTH_CHANGE_EVENT,
  clearAllStorageAndCache,
  getAuthToken,
  redirectToSigninAfterAuthFailure,
} from '@/lib/auth'
import { resolveB2CRouteGuardView } from '@/lib/b2cRouteAccess'

function RouteAccessPendingScreen() {
  return (
    <div
      className="flex min-h-screen w-full items-center justify-center"
      role="status"
      aria-live="polite"
    >
      <Loader2 className="h-8 w-8 animate-spin text-[#6155F5]" aria-hidden="true" />
      <span className="sr-only">Loading…</span>
    </div>
  )
}

/**
 * Keeps B2C-only pages (see B2C_ONLY_ROUTES) unmounted for enterprise owner/recruiter sessions,
 * so no restricted content renders and no page effects (API calls, checkout init) run.
 *
 * - Hard load: the server cannot read localStorage, so restricted routes render a neutral
 *   loader until mount, then the decision below runs. Public/SEO routes keep their server HTML;
 *   the pre-paint <head> script already hid and redirected enterprise sessions on those.
 * - Client navigation: the guard is already mounted, so the decision runs during render and the
 *   page never mounts for a disallowed session.
 * - Signed-out visitors are allowed through; TokenValidator and each page's existing login
 *   handling apply unchanged.
 */
export default function B2CRouteGuard({ children }: { children: ReactNode }) {
  const pathname = usePathname()
  const router = useRouter()
  const [hasMounted, setHasMounted] = useState(false)
  // Bumped on token changes (login/logout here or in another tab) so the decision is re-read.
  const [, setAuthRevision] = useState(0)

  useEffect(() => {
    setHasMounted(true)
    const handleAuthChange = () => setAuthRevision((revision) => revision + 1)
    window.addEventListener(AUTH_CHANGE_EVENT, handleAuthChange)
    window.addEventListener('storage', handleAuthChange)
    return () => {
      window.removeEventListener(AUTH_CHANGE_EVENT, handleAuthChange)
      window.removeEventListener('storage', handleAuthChange)
    }
  }, [])

  const { renderPage, routeAccess } = resolveB2CRouteGuardView(
    pathname,
    hasMounted,
    getAuthToken,
  )
  const redirectDestination =
    routeAccess?.kind === 'redirect' ? routeAccess.destination : null
  const isInvalidSession = routeAccess?.kind === 'invalid-session'

  useEffect(() => {
    if (redirectDestination) {
      router.replace(redirectDestination)
    }
  }, [redirectDestination, pathname, router])

  useEffect(() => {
    if (isInvalidSession) {
      void clearAllStorageAndCache().then(() => redirectToSigninAfterAuthFailure())
    }
  }, [isInvalidSession, pathname])

  return renderPage ? <>{children}</> : <RouteAccessPendingScreen />
}
